///
/// EarthBrowser
///
/// Created by Matt Giger on 8/24/2024
/// Copyright (c) 2024 Geodata Labs
///

import { StovokorContext } from "@stovokor/StovokorContext"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { type Geometry } from "@stovokor/shader/Geometry"
import { BlendFactor } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { ActionContainer, disableScissorTest, enableScissorTest, type ShaderAction, type ShaderActions } from "@stovokor/shader/ShaderActions"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type VertexArray } from "@stovokor/shader/VertexArray"

const cNoActions: ShaderActions = []

/**
 * Fixed-function state a renderer draws with. Declared rather than toggled
 * with pre/post actions so the state cache can diff it between consecutive
 * draws: two hundred double-sided glyphs in a row cost one cull-face change
 * instead of four hundred. Unset fields mean the engine default.
 */
export interface DrawState {
	/** Back-face culling; default on */
	cullFace?: boolean
	/** Depth test; default on */
	depthTest?: boolean
	/** Depth writes; default follows the pass: on for opaque, off for transparent */
	depthWrite?: boolean
	/** Blend factors applied to colour and alpha; default SRC_ALPHA / ONE_MINUS_SRC_ALPHA colour with ONE / ONE alpha */
	blend?: [BlendFactor, BlendFactor]
	/**
	 * Depth bias as [factor, units] (gl.polygonOffset); negative values pull
	 * the fragments toward the camera. `units` is in multiples of the depth
	 * buffer's resolvable step, so the bias tracks the buffer's precision at
	 * the fragment's distance — a surface draped a fixed height above terrain
	 * stops z-fighting at long range without being lifted further. Default off
	 */
	polygonOffset?: [number, number]
}

export class RenderActionSet {
	/** View-space depth (distance in front of the camera); see NodeBase.depth */
	public depth: number = 0
	/** Primary sort key within a pass, lower draws first; see NodeBase.renderOrder */
	public order: number = 0
	/**
	 * Shader program id. The opaque pass groups draws by it (depth order
	 * within a group) so consecutive draws share a program; the depth test
	 * makes the result identical and early-z still applies within a group
	 */
	public program: number = 0
	public transparency: boolean
	public actions: ShaderActions = []

	constructor(depth: number, transparency: boolean, actions: ShaderActions, order: number = 0, program: number = 0) {
		this.depth = depth
		this.order = order
		this.program = program
		this.transparency = transparency
		this.actions = actions
	}

	public render(): void {
		const actions = this.actions
		for(let i = 0; i < actions.length; i++) {
			actions[i]()
		}
	}
}

/**
 * The Renderer class is the base class for all renderers.
 *
 * It will extract dynamic uniforms from the given objects and itself and add them to the dynamics array.
 * Therefore it is very important that your UniformDynamic objects are public and named the same as the uniforms in the shader.
 *
 * The per-draw action list (draw state, pre, bind, dynamic uniforms, draw,
 * post) is assembled once when the renderer becomes available and reused
 * every frame. Fixed-function state belongs in `drawState` (set in the
 * constructor; it is read at draw time so later changes apply too).
 * `preActions` / `postActions` remain for anything else and run after the
 * draw state is applied; a subclass that changes them after the first
 * render must call `invalidateRenderActions()`. `renderAction` may be
 * reassigned freely.
 */
export abstract class Renderer {
	public shader: ShaderProgram
	public shaderVersion: number = 0
	public geometry: Geometry
	protected materials: Material[] = []
	protected available: boolean = false

	protected readonly drawState: DrawState = {}
	protected preActions: ShaderActions = []
	protected bindActions: ShaderActions = []
	protected dynamics: ActionContainer[] = []
	protected renderAction: ShaderAction = () => {}
	protected postActions: ShaderActions = []

	protected transparency: boolean = false

	private context: StovokorContext
	private vertexArray?: VertexArray
	// Flattened action list, rebuilt only when the shader relinks or a
	// subclass asks for it; `renderAction` is reached through a trampoline
	// so subclasses may swap it per frame without invalidating the list
	private actions: ShaderActions = cNoActions
	private actionsDirty: boolean = true
	private readonly drawAction: ShaderAction = () => this.renderAction()

	// Asserts the full fixed-function state for this draw through the state
	// cache, so it only reaches the driver when the previous draw differed
	private readonly applyDrawState: ShaderAction = () => {
		const glState = this.context.glState
		const draw = this.drawState
		glState.setEnabled(WebGL2RenderingContext.CULL_FACE, draw.cullFace ?? true)
		glState.setEnabled(WebGL2RenderingContext.DEPTH_TEST, draw.depthTest ?? true)
		glState.depthMask(draw.depthWrite ?? glState.passDepthWrite)
		const offset = draw.polygonOffset
		glState.setEnabled(WebGL2RenderingContext.POLYGON_OFFSET_FILL, offset !== undefined)
		if(offset !== undefined) {
			glState.polygonOffset(offset[0], offset[1])
		}
		if(draw.blend !== undefined) {
			glState.blendFunc(draw.blend[0], draw.blend[1])
		} else {
			glState.blendFuncSeparate(BlendFactor.SrcAlpha, BlendFactor.OneMinusSrcAlpha, BlendFactor.One, BlendFactor.One)
		}
	}

	constructor(context: StovokorContext, source: ShaderSourceDefinition, geometry: Geometry, materials: Material | Material[] = []) {
		this.context = context
		this.shader = context.getCachedShader(source)
		this.shaderVersion = this.shader.version
		this.geometry = geometry
		this.materials = Array.isArray(materials) ? materials : [materials]
		this.transparency = this.anyTransparent()
	}

	public destroy(): void {
		this.releaseVertexArray()
		this.geometry.destroy()
	}

	private releaseVertexArray(): void {
		if(this.vertexArray !== undefined) {
			this.context.vertexArrays.release(this.geometry, this.vertexArray)
			this.vertexArray = undefined
		}
	}

	protected setupRenderActions(): void {
		// Attribute locations are per program: take the array for this
		// (link, geometry) on every (re)link rather than only on first use.
		// It is shared with every other renderer of the same pair, so
		// consecutive draws of one mesh keep the array bound
		this.releaseVertexArray()
		this.vertexArray = this.context.vertexArrays.acquire(this.shader, this.geometry)

		this.bindActions = [
			this.shader.bindAction(),
			this.vertexArray.bindAction(),
			...this.shader.setupUniformActionsFrom(this.materials)
		]

		const geometry = this.geometry
		if(geometry.instanceCount !== undefined) {
			const instances = () => geometry.instanceCount ?? 0
			this.renderAction = geometry.indexBuffer
				? geometry.indexBuffer.drawInstancedAction(geometry.mode, instances)
				: geometry.vertexBuffer.drawInstancedAction(geometry.mode, instances)
		} else if(geometry.indexBuffer) {
			this.renderAction = geometry.indexBuffer.drawAction(geometry.mode)
		} else {
			this.renderAction = geometry.vertexBuffer.drawAction(geometry.mode)
		}

		// Attribute enables live in the vertex array, so nothing needs
		// disabling after the draw; the viewport unbinds the array when its
		// pass ends so offscreen passes start from the default one

		this.dynamics = []
		this.extractDynamicUniforms([this, this.geometry, ...this.materials])
		this.actionsDirty = true

		this.prepare()
	}

	/** Rebuild the flattened action list before the next render */
	protected invalidateRenderActions(): void {
		this.actionsDirty = true
	}

	private buildActions(): void {
		this.actions = [
			this.applyDrawState,
			...this.preActions,
			...this.bindActions,
			...this.dynamics.map(wrapper => wrapper.action),
			this.drawAction,
			...this.postActions
		]
		this.actionsDirty = false
	}

	private anyTransparent(): boolean {
		for(const material of this.materials) {
			if(material.transparency) {
				return true
			}
		}
		return false
	}

	private allAvailable(): boolean {
		for(const material of this.materials) {
			if(!material.available) {
				return false
			}
		}
		return true
	}

	/**
	 * Extracts dynamic uniforms from the given objects and adds them to the dynamics array.
	 * @param objects - The objects to extract dynamic uniforms from.
	 */
	private extractDynamicUniforms(objects: object[]): void {
		objects.forEach(object => {
			Object.entries(object).forEach(([name, property]) => {
				if(property instanceof UniformDynamic) {
					const uniform = this.shader.uniforms[name]
					if(uniform) {
						property.setBinding(uniform)
						this.dynamics.push(property)
					} else {
						this.context.logger.warn(`Uniform ${name} not found in shader`)
					}
				}
			})
		})
	}

	protected prepare(): void {}

	protected abstract stateUpdate(state: RenderState): void

	public render(state: RenderState, depth: number, order: number = 0): RenderActionSet {

		if(!this.available) {
			if(this.shader.available && this.geometry.available && this.allAvailable()) {
				this.setupRenderActions()
				this.stateUpdate(state)
				this.available = true
			} else {
				return new RenderActionSet(depth, false, cNoActions, order)
			}
		} else {
			// check if the shader has been updated
			if(this.shaderVersion !== this.shader.version) {
				this.shaderVersion = this.shader.version
				if(!this.shader.available) {
					// A relink that failed: draw nothing until it compiles again
					return new RenderActionSet(depth, false, cNoActions, order)
				}
				this.setupRenderActions()
			} else if(!this.shader.available) {
				return new RenderActionSet(depth, false, cNoActions, order)
			}
			this.stateUpdate(state)
		}

		if(this.actionsDirty) {
			this.buildActions()
		}

		// Update transparency state each frame to handle dynamic alpha changes
		this.transparency = this.anyTransparent()

		if(state.clipRect) {
			// Scissor to the clip rect; the rect varies per call so this
			// list is assembled on demand (UI panes only)
			return new RenderActionSet(depth, this.transparency, [
				enableScissorTest(this.context, state.clipRect, state.height),
				...this.actions,
				disableScissorTest(this.context)
			], order, this.shader.id)
		}

		return new RenderActionSet(depth, this.transparency, this.actions, order, this.shader.id)
	}
}

export class SimpleRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()

	constructor(context: StovokorContext, geometry: Geometry, material: Material, drawState?: DrawState) {
		super(context, new ShaderSourceDefinition("simple_color"), geometry, [material])
		if(drawState !== undefined) {
			Object.assign(this.drawState, drawState)
		}
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

export class SimpleTexRenderer extends Renderer {
	private modelViewProjection = new UniformDynamic<matrix>()

	constructor(context: StovokorContext, geometry: Geometry, material: Material) {
		super(context, new ShaderSourceDefinition("simple_tex"), geometry, [material])
	}

	override setupRenderActions() {
		super.setupRenderActions()
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}